import { Link } from '@mui/material'
import { useAppSelector } from '../../../../redux/store'
import { selectCustomerTrainings, selectUserTrainingMetadata } from '../slice'
import CompliancePlip from '../../../../components/CompliancePlip'
import { ComplianceStatus, UserTrainingMetadata } from '../../../../types'
import { getFullNameOrEmail, selectCurrentUser, selectIsAdminOrOwner } from '../../../../redux/user'
import { useEffect, useState } from 'react'
import dayjs from 'dayjs'
import useCustomer from '../../../../hooks/useCustomer'

interface TrainingProps {
  link: string
}

const Training = ({ link }: TrainingProps) => {
  const customer = useCustomer()
  const metadata = customer.customerMetadata
  const isAdminOrOwner = useAppSelector(selectIsAdminOrOwner)
  const currentUser = useAppSelector(selectCurrentUser)
  const customerTrainings = useAppSelector(selectCustomerTrainings)
  const userTrainingMetadata = useAppSelector(selectUserTrainingMetadata)

  const [trainingUpdatedDate, setTrainingUpdatedDate] = useState<string | null>(null)
  const [currentUserMetadata, setCurrentUserMetadata] = useState<UserTrainingMetadata | null>(null)

  useEffect(() => {
    if (!metadata || !customer || !userTrainingMetadata || !currentUser) return

    const currentUserId = Number(currentUser.login_user_id)
    const userMetadata =
      userTrainingMetadata.find(m => Number(m.user_id) === currentUserId) ?? null

    let dateToFormat: Date | null | string = null

    if (isAdminOrOwner) {
      dateToFormat = userTrainingMetadata.reduce<Date | null>((max, m) => {
        const updatedDate = new Date(m.updated_date)
        if (!max) return updatedDate
        return updatedDate > max ? updatedDate : max
      }, null)
    } else if (userMetadata?.updated_date) {
      dateToFormat = userMetadata.updated_date
    }

    setTrainingUpdatedDate(dateToFormat ? dayjs(dateToFormat).format('MMM D, YYYY') : null)
    setCurrentUserMetadata(userMetadata)
  }, [userTrainingMetadata, isAdminOrOwner, currentUser, metadata])

  return metadata && customer && customerTrainings && currentUser && (
    <>
      {isAdminOrOwner ? (
        <>
          <div className='border-box'>
            <div>
              <CompliancePlip message='' className='dashboard-compliance' status={metadata.training_compliance_status} />
            </div>
            <div className='compliance-message'>
              {metadata.training_compliance_status === ComplianceStatus.green && (
                <p>All required training is complete for the practice <Link href={link} className="text-info small-text"> View Details</Link></p>
              )}
              {metadata.training_compliance_status === ComplianceStatus.yellow && (
                <p>The practice has {metadata.training_due_soon_count} training(s) due soon <Link href={link} className="text-info small-text"> View Details</Link></p>
              )}
              {metadata.training_compliance_status === ComplianceStatus.red && (
                <p>The practice has {metadata.training_past_due_count} training(s) past due <Link href={link} className="text-info small-text"> View Details</Link></p>
              )}
              {customerTrainings.length > 0 && metadata.training_compliance_status === ComplianceStatus.Inactive && (
                <p>Training is available and can be assigned <Link href={link} className="text-info small-text"> View Details</Link></p>
              )}
              {customerTrainings.length === 0 && (
                <p>There are currently no required trainings for the practice <Link href={link} className="text-info small-text"> Learn More</Link></p>
              )}
            </div>
          </div>
          <div className="card-footer flex justify-between">
            {metadata.training_past_due_count > 0 && (
              <div className="center-text">
                <span className="block text-ot-dashboard">
                  {metadata.training_past_due_count}<CompliancePlip message='' className='dashboard-compliance small' status={ComplianceStatus.red} />
                </span>
                <span className="text-base">Action Required</span>
              </div>
            )}
            {metadata.training_due_soon_count > 0 && (
              <div className="center-text">
                <span className="block text-ot-dashboard">
                  {metadata.training_due_soon_count}<CompliancePlip message='' className='dashboard-compliance small' status={ComplianceStatus.yellow} />
                </span>
                <span className="text-base">Alerts</span>
              </div>
            )}
            {trainingUpdatedDate && (
              <div className="center-text">
                <span className="block text-lg">
                  {trainingUpdatedDate}
                </span>
                <span className="text-base">Last Activity</span>
              </div>
            )}
          </div>
        </>
      ) : (
        <>
          <div className='border-box'>
            <div>
              <CompliancePlip message='' className='dashboard-compliance' status={currentUserMetadata?.compliance?.compliance_status ?? ComplianceStatus.Inactive} />
            </div>
            <div className='compliance-message'>
              {currentUserMetadata?.compliance?.compliance_status === ComplianceStatus.green && (
                <p>All required training is complete for <span>{getFullNameOrEmail(currentUser)}</span><Link href={link} className="text-info small-text"> View Details</Link></p>
              )}
              {currentUserMetadata?.compliance?.compliance_status === ComplianceStatus.yellow && (
                <p>{currentUserMetadata?.due_soon_count} training(s) due soon for <span>{getFullNameOrEmail(currentUser)}</span><Link href={link} className="text-info small-text"> View Details</Link></p>
              )}
              {currentUserMetadata?.compliance?.compliance_status === ComplianceStatus.red && (
                <p>{currentUserMetadata.past_due_count} training(s) past due for <span>{getFullNameOrEmail(currentUser)}</span><Link href={link} className="text-info small-text"> View Details</Link></p>
              )}
              {customerTrainings.length > 0 && (!currentUserMetadata || currentUserMetadata?.compliance?.compliance_status === ComplianceStatus.Inactive) && (
                <p>There are no active/required trainings for <span>{getFullNameOrEmail(currentUser)}</span><Link href={link} className="text-info small-text"> View Details</Link></p>
              )}
              {customerTrainings.length === 0 && (
                <p>This practice is not using the training service<Link href={link} className="text-info small-text"> Learn More</Link></p>
              )}
            </div>
          </div>
          <div className="card-footer flex justify-between">
            {currentUserMetadata?.past_due_count ? (
              <div className="center-text">
                <span className="block text-ot-dashboard">{currentUserMetadata?.past_due_count}</span>
                <span className="text-base"><CompliancePlip message='' className='dashboard-compliance small' status={ComplianceStatus.red} />Action Required</span>
              </div>
            ) : null}
            {currentUserMetadata?.due_soon_count ? (
              <div className="center-text">
                <span className="block text-ot-dashboard">{currentUserMetadata?.due_soon_count}</span>
                <span className="text-base"><CompliancePlip message='' className='dashboard-compliance small' status={ComplianceStatus.yellow} />Alerts</span>
              </div>
            ) : null}
            {trainingUpdatedDate !== null && (
              <div className="center-text">
                <span className="block text-ot-dashboard">{trainingUpdatedDate}</span>
                <span className="text-base">Last Activity</span>
              </div>
            )}
          </div>
        </ >
      )}
    </>
  )
}

export default Training